"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useMemo, useState } from "react";
import * as BasePhoneInput from "react-phone-number-input";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, ComboboxSeparator, ComboboxTrigger, ComboboxValue } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GlobeIcon } from "lucide-react";
import Image from "next/image";
type PhoneInputSize = "sm" | "default" | "lg";
const PhoneInputContext = createContext<{
  variant: PhoneInputSize;
  popupClassName?: string;
  scrollAreaClassName?: string;
}>({
  variant: "default",
  popupClassName: undefined,
  scrollAreaClassName: undefined
});
type PhoneInputProps = Omit<React.ComponentProps<"input">, "onChange" | "value" | "ref"> & Omit<BasePhoneInput.Props<typeof BasePhoneInput.default>, "onChange" | "variant" | "popupClassName" | "scrollAreaClassName"> & {
  onChange?: (value: BasePhoneInput.Value) => void;
  variant?: PhoneInputSize;
  popupClassName?: string;
  scrollAreaClassName?: string;
};
function PhoneInput(t0) {
  const $ = _c(24);
  let T0;
  let T1;
  let onChange;
  let props;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    const {
      className,
      variant,
      popupClassName,
      scrollAreaClassName,
      onChange: t3,
      value: t4,
      ...t5
    } = t0;
    onChange = t3;
    value = t4;
    props = t5;
    const phoneInputSize = variant || "default";
    T1 = PhoneInputContext.Provider;
    if ($[8] !== phoneInputSize || $[9] !== popupClassName || $[10] !== scrollAreaClassName) {
      t2 = {
        variant: phoneInputSize,
        popupClassName,
        scrollAreaClassName
      };
      $[8] = phoneInputSize;
      $[9] = popupClassName;
      $[10] = scrollAreaClassName;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    T0 = BasePhoneInput.default;
    t1 = cn("flex", props["aria-invalid"] && "[&_*[data-slot=combobox-trigger]]:border-destructive [&_*[data-slot=combobox-trigger]]:ring-destructive/50", className);
    $[0] = t0;
    $[1] = T0;
    $[2] = T1;
    $[3] = onChange;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = value;
  } else {
    T0 = $[1];
    T1 = $[2];
    onChange = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    value = $[7];
  }
  const t3 = value || undefined;
  let t4;
  if ($[12] !== onChange) {
    t4 = value_0 => onChange?.(value_0 || "" as BasePhoneInput.Value);
    $[12] = onChange;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== T0 || $[15] !== props || $[16] !== t1 || $[17] !== t3 || $[18] !== t4) {
    t5 = <T0 className={t1} flagComponent={FlagComponent} countrySelectComponent={CountrySelect} inputComponent={InputComponent} smartCaret={false} value={t3} onChange={t4} {...props} />;
    $[14] = T0;
    $[15] = props;
    $[16] = t1;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== T1 || $[21] !== t2 || $[22] !== t5) {
    t6 = <T1 value={t2}>{t5}</T1>;
    $[20] = T1;
    $[21] = t2;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function InputComponent(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    variant
  } = useContext(PhoneInputContext);
  const t1 = variant === "sm" && "h-7";
  const t2 = variant === "lg" && "h-9";
  let t3;
  if ($[3] !== className || $[4] !== t1 || $[5] !== t2) {
    t3 = cn("ring-none! rounded-s-none outline-none! focus:z-1", t1, t2, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t3) {
    t4 = <Input className={t3} {...props} />;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
type CountryEntry = {
  label: string;
  value: BasePhoneInput.Country | undefined;
};
type CountrySelectProps = {
  disabled?: boolean;
  value: BasePhoneInput.Country;
  options: CountryEntry[];
  onChange: (country: BasePhoneInput.Country) => void;
};
function CountrySelect(t0) {
  const $ = _c(38);
  const {
    disabled,
    value: selectedCountry,
    options: countryList,
    onChange
  } = t0;
  const {
    variant,
    popupClassName
  } = useContext(PhoneInputContext);
  const [searchValue, setSearchValue] = useState("");
  let t1;
  bb0: {
    if (!searchValue) {
      t1 = countryList;
      break bb0;
    }
    let t2;
    if ($[0] !== countryList || $[1] !== searchValue) {
      let t3;
      if ($[3] !== searchValue) {
        t3 = t4 => {
          const {
            label
          } = t4;
          return label.toLowerCase().includes(searchValue.toLowerCase());
        };
        $[3] = searchValue;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = countryList.filter(t3);
      $[0] = countryList;
      $[1] = searchValue;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const filteredCountries = t1;
  const t2 = selectedCountry || "";
  let t3;
  if ($[5] !== onChange) {
    t3 = country => {
      if (country) {
        onChange(country);
      }
    };
    $[5] = onChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = disabled && "opacity-50";
  let t5;
  if ($[7] !== t4) {
    t5 = cn("flex gap-1 rounded-s-lg rounded-e-none border-e-0 px-2.5 py-0 leading-none hover:bg-transparent focus:z-10 data-pressed:bg-transparent", t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="sr-only"><ComboboxValue /></span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== selectedCountry) {
    t7 = <FlagComponent country={selectedCountry} countryName={selectedCountry} />;
    $[10] = selectedCountry;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== disabled || $[13] !== t5 || $[14] !== t7 || $[15] !== variant) {
    t8 = <ComboboxTrigger render={<Button variant="outline" size={variant} className={t5} disabled={disabled} title="country-flag">{t6}{t7}</Button>} />;
    $[12] = disabled;
    $[13] = t5;
    $[14] = t7;
    $[15] = variant;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== popupClassName) {
    t9 = cn("w-xs *:data-[slot=input-group]:bg-transparent", popupClassName);
    $[17] = popupClassName;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e => setSearchValue(e.target.value);
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== searchValue) {
    t11 = <ComboboxInput placeholder="e.g. United States" value={searchValue} onChange={t10} showTrigger={false} className="rounded-none border-0 border-input px-0 py-2.5 shadow-none ring-0! outline-none! focus-visible:border-border focus-visible:ring-0 focus-visible:ring-offset-0" />;
    $[20] = searchValue;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ComboboxSeparator />;
    t13 = <ComboboxEmpty className="px-4 py-2.5 text-sm">No country found.</ComboboxEmpty>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  let t14;
  if ($[24] !== filteredCountries) {
    t14 = filteredCountries.map(_temp);
    $[24] = filteredCountries;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <ComboboxList><div className="relative flex max-h-full"><div className="flex max-h-[min(var(--available-height),24rem)] w-full scroll-pt-2 scroll-pb-2 flex-col overscroll-contain"><ScrollArea className="size-full min-h-0 **:data-[slot=scroll-area-scrollbar]:m-0 [&_[data-slot=scroll-area-viewport]]:h-full [&_[data-slot=scroll-area-viewport]]:overscroll-contain">{t14}</ScrollArea></div></div></ComboboxList>;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t11 || $[29] !== t15 || $[30] !== t9) {
    t16 = <ComboboxContent className={t9}>{t11}{t12}{t13}{t15}</ComboboxContent>;
    $[28] = t11;
    $[29] = t15;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== filteredCountries || $[33] !== t16 || $[34] !== t2 || $[35] !== t3 || $[36] !== t8) {
    t17 = <Combobox items={filteredCountries} value={t2} onValueChange={t3}>{t8}{t16}</Combobox>;
    $[32] = filteredCountries;
    $[33] = t16;
    $[34] = t2;
    $[35] = t3;
    $[36] = t8;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
}
function _temp(item) {
  return item.value ? <ComboboxItem key={item.value} value={item.value} className="flex items-center gap-2"><FlagComponent country={item.value} countryName={item.label} /><span className="flex-1 text-sm">{item.label}</span><span className="text-sm text-foreground/50">{`+${BasePhoneInput.getCountryCallingCode(item.value)}`}</span></ComboboxItem> : null;
}
function FlagComponent(t0) {
  const $ = _c(4);
  const {
    country,
    countryName
  } = t0;
  if (!country) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <GlobeIcon className="size-4 opacity-60" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = `https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`;
  let t2;
  if ($[1] !== countryName || $[2] !== t1) {
    t2 = <span className="flex h-4 w-6 items-center justify-center overflow-hidden"><Image src={t1} alt={countryName} title={countryName} width={24} height={16} className="h-4 w-6 rounded-xs object-cover" /></span>;
    $[1] = countryName;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export { PhoneInput };